import { useEffect, useId, useRef, useState } from "react";
import { useFetcher, useParams } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import type { Invitation } from "@repo/shared-types";
import { queries } from "@/lib/queries";
import { readPages } from "@/lib/query";
import { SegmentedControl } from "@/components/form";
import { ShowMore, rowsOf } from "@/components/show-more";
import { RoleBadge } from "@/components/member-sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Item, ItemActions, ItemContent, ItemTitle } from "@/components/ui/item";
import {
  SectionHeader,
  backToSpace,
  loadSection,
  useSection,
  type ActionResult,
} from "@/routes/space-section";

/**
 * Inviting somebody into a space, and the invitations still waiting for an
 * answer: the owner's, and an admin's holding manage_members — who invites
 * members only.
 *
 * One box takes a username or an email address (decided with Tiago 12
 * October): the action tells them apart, by an `@` after the first
 * character, so nobody has to choose which box first.
 */

export async function inviteLoader({ request, params }: LoaderFunctionArgs) {
  const section = await loadSection(request, params, "invite");
  if (!section.canManageMembers) throw backToSpace(section);
  await readPages(queries.spaceInvitations(params.slug ?? ""));
  return null;
}

export default function SpaceInvite() {
  const slug = useParams().slug ?? "";
  const { tenant, isOwner } = useSection(slug);
  const pages = useSuspenseInfiniteQuery(queries.spaceInvitations(slug));
  const invitations = rowsOf(pages.data, (page) => page.invitations, invitationKey);

  return (
    <section className="space-y-6">
      <SectionHeader tenant={tenant} title="Invite" />
      {/* Only an owner may mint an admin. */}
      <InviteForm mayInviteAdmins={isOwner} />

      {invitations.length > 0 && (
        <div className="space-y-3">
          <h2 className="section-label">Waiting for an answer</h2>
          <Card className="gap-0 py-1.5">
            <ul className="divide-y divide-border">
              {invitations.map((invitation) => (
                <li key={invitationKey(invitation)}>
                  <Item className="flex-nowrap rounded-none">
                    <ItemContent className="min-w-0">
                      <ItemTitle className="line-clamp-none w-auto break-all">
                        {invitation.email ?? `@${invitation.username}`}
                      </ItemTitle>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <RoleBadge role={invitation.role} />
                        expires {new Date(invitation.expiresAt).toLocaleDateString()}
                      </div>
                    </ItemContent>
                    {/* Withdrawing an invitation to become an admin is the
                        owner's, like sending one. */}
                    {(isOwner || invitation.role === "member") && (
                      <ItemActions>
                        <Withdraw invitation={invitation} />
                      </ItemActions>
                    )}
                  </Item>
                </li>
              ))}
            </ul>
          </Card>
          <ShowMore query={pages} />
        </div>
      )}
    </section>
  );
}

/** An invitation has no id: it is what it is addressed to, and when it was sent. */
function invitationKey(invitation: Invitation): string {
  return (invitation.email ?? invitation.username ?? "") + invitation.createdAt;
}

type Role = "member" | "admin";

/**
 * The box, who they join as, and Invite. Sent by fetcher, so what goes wrong
 * is said under the box; once it is sent the box empties for the next person
 * and the role goes back to Member, and the action's toast says it went.
 */
function InviteForm({ mayInviteAdmins }: { mayInviteAdmins: boolean }) {
  const fetcher = useFetcher<ActionResult>();
  const form = useRef<HTMLFormElement>(null);
  const [role, setRole] = useState<Role>("member");
  const id = useId();
  const busy = fetcher.state !== "idle";
  const error = fetcher.data?.error;

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.ok) {
      form.current?.reset();
      setRole("member");
    }
  }, [fetcher.state, fetcher.data]);

  return (
    <Card>
      <CardContent>
        <fetcher.Form ref={form} method="post" className="space-y-6">
          <input type="hidden" name="intent" value="invite" />
          <Field data-invalid={error ? true : undefined}>
            <FieldLabel htmlFor={id}>Username or email address</FieldLabel>
            <Input
              id={id}
              name="invitee"
              required
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              aria-invalid={error ? true : undefined}
              aria-describedby={`${id}-says`}
            />
            {error ? (
              <FieldError id={`${id}-says`}>{error}</FieldError>
            ) : (
              <FieldDescription id={`${id}-says`}>
                By username if they have an account, by email address if they do not. Either way they choose
                whether to accept.
              </FieldDescription>
            )}
          </Field>

          {mayInviteAdmins ? (
            <SegmentedControl<Role>
              label="As"
              name="role"
              options={[
                { value: "member", label: "Member" },
                { value: "admin", label: "Admin" },
              ]}
              value={role}
              onChange={setRole}
            />
          ) : (
            <input type="hidden" name="role" value="member" />
          )}

          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {busy ? "Inviting…" : "Invite"}
          </Button>
        </fetcher.Form>
      </CardContent>
    </Card>
  );
}

/** Takes one invitation back, by whatever it was addressed to. */
function Withdraw({ invitation }: { invitation: Invitation }) {
  const fetcher = useFetcher<ActionResult>();
  return (
    <fetcher.Form method="post" className="flex shrink-0 flex-col items-end gap-1">
      <input type="hidden" name="intent" value="revoke" />
      <input type="hidden" name="email" value={invitation.email ?? ""} />
      <input type="hidden" name="username" value={invitation.username ?? ""} />
      <Button type="submit" variant="outline" size="sm" disabled={fetcher.state !== "idle"}>
        Withdraw
      </Button>
      {fetcher.data?.error && <p className="max-w-40 text-right text-sm text-destructive">{fetcher.data.error}</p>}
    </fetcher.Form>
  );
}
